import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, MapPin, Search, Filter } from 'lucide-react';
import { motion } from 'framer-motion';

export default function BusinessListPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [searchParams]);

  const fetchMetadata = async () => {
    try {
      const [categoriesRes, citiesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/cities`),
      ]);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      if (searchParams.get('city')) params.append('city', searchParams.get('city'));

      const response = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (selectedCategory) params.append('category', selectedCategory);
    if (selectedCity) params.append('city', selectedCity);
    navigate(`/businesses?${params.toString()}`);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || '';
  };

  const getCityName = (cityId) => {
    const city = cities.find((c) => c.id === cityId);
    return city?.name || '';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Filters */}
        <Card className="p-6 mb-8 shadow-sm" data-testid="filter-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold">Filter Businesses</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center border rounded-lg px-3 bg-white">
              <Search className="w-4 h-4 text-slate-400 mr-2" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 focus-visible:ring-0"
                data-testid="search-filter-input"
              />
            </div>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger data-testid="city-filter">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleFilter}
              className="rounded-lg bg-primary hover:bg-primary/90"
              data-testid="apply-filter-button"
            >
              Apply Filters
            </Button>
          </div>
        </Card>

        {/* Results */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="results-title">
            {businesses.length} Business{businesses.length !== 1 ? 'es' : ''} Found
          </h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-slate-600 text-lg">No businesses found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {businesses.map((business, index) => (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card
                  onClick={() => navigate(`/business/${business.id}`)}
                  className="group relative overflow-hidden rounded-2xl cursor-pointer border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  data-testid={`business-card-${business.id}`}
                >
                  <div
                    className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{
                      backgroundImage: `url(${business.logo || 'https://images.unsplash.com/photo-1646602201694-43d9acc2b542?w=400'})`,
                    }}
                  ></div>

                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2 line-clamp-1">{business.name}</h3>

                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">
                        {business.rating || 0} ({business.review_count || 0} reviews)
                      </span>
                    </div>

                    <p className="text-sm text-slate-600 mb-3">
                      {getCategoryName(business.category_id)} • {getCityName(business.city_id)}
                    </p>

                    <div className="flex items-start text-slate-600 text-sm">
                      <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{business.address}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
